import { Modal, cn } from '@ambernord/ui';
import { Boxes, CornerDownLeft, type LucideIcon, Search } from 'lucide-react';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { isCarrier, isPlatform, isSupplier, visibleGroups } from './nav.js';

/**
 * ⌘K / Ctrl+K komandpalete: izplūdusi (fuzzy) navigācijas meklēšana + piegādes
 * pasūtījumu meklēšana pēc `ref` (≥3 rakstzīmes; klienta pusē filtrēts saraksts no
 * GET /api/supply-orders — atbilde `{ items: [{ id, ref, status }] }`).
 * Tastatūra: ↑/↓ + Enter naviģē, Esc aizver (Modal to jau apstrādā).
 */

interface PaletteResult {
  id: string;
  to: string;
  label: string;
  sub?: string;
  icon: LucideIcon;
  section: 'nav' | 'order';
}

interface SupplyOrderLite {
  id: string;
  ref: string;
  status: string;
}

/** Vienkārša subsecence-fuzzy sakritība (reģistrnejutīga). */
function fuzzyMatch(query: string, text: string): boolean {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  let i = 0;
  for (const ch of q) {
    i = t.indexOf(ch, i);
    if (i < 0) return false;
    i += 1;
  }
  return true;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role ?? '';

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [orders, setOrders] = useState<SupplyOrderLite[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(false);

  const canSearchOrders = isPlatform(role) || isSupplier(role) || isCarrier(role);

  // Atverot — tīrs stāvoklis + fokuss ievadē.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(0);
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  // ≥3 rakstzīmes → vienreiz ielādē piegādes pasūtījumus (kešo sesijas ietvaros).
  useEffect(() => {
    if (!open || query.trim().length < 3 || !canSearchOrders || fetchedRef.current) return;
    fetchedRef.current = true;
    void api.get<{ items?: SupplyOrderLite[] }>('/api/supply-orders').then((res) => {
      if (res.ok && Array.isArray(res.data.items)) {
        setOrders(res.data.items.map(({ id, ref, status }) => ({ id, ref, status })));
      }
    });
  }, [open, query, canSearchOrders]);

  const navResults = useMemo<PaletteResult[]>(() => {
    const q = query.trim();
    const out: PaletteResult[] = [];
    for (const group of visibleGroups(role)) {
      for (const item of group.items) {
        const label = t(`nav.${item.key}`);
        if (q === '' || fuzzyMatch(q, label) || fuzzyMatch(q, item.to)) {
          out.push({
            id: `nav:${item.to}`,
            to: item.to,
            label,
            sub: t(`navGroups.${group.key}`),
            icon: item.icon,
            section: 'nav',
          });
        }
      }
    }
    return out;
  }, [query, role, t]);

  const orderResults = useMemo<PaletteResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 3) return [];
    return orders
      .filter((o) => o.ref.toLowerCase().includes(q))
      .slice(0, 8)
      .map((o) => ({
        id: `so:${o.id}`,
        to: `/supply-orders/${o.id}`,
        label: o.ref,
        sub: o.status,
        icon: Boxes,
        section: 'order' as const,
      }));
  }, [query, orders]);

  const results = useMemo(() => [...navResults, ...orderResults], [navResults, orderResults]);

  // Izvēle nedrīkst izkrist ārpus saraksta, kad rezultāti mainās.
  useEffect(() => {
    setSelected((s) => (results.length === 0 ? 0 : Math.min(s, results.length - 1)));
  }, [results.length]);

  const go = (r: PaletteResult | undefined) => {
    if (!r) return;
    onClose();
    navigate(r.to);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => (results.length === 0 ? 0 : (s + 1) % results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => (results.length === 0 ? 0 : (s - 1 + results.length) % results.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[selected]);
    }
  };

  // Ieritina izvēlēto rindu redzamībā (pogas seko `results` secībai).
  useEffect(() => {
    listRef.current?.querySelectorAll('button')[selected]?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const renderSection = (section: 'nav' | 'order', heading: string) => {
    const items = results.filter((r) => r.section === section);
    if (items.length === 0) return null;
    return (
      <div key={section}>
        <p className="px-4 pb-1 pt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
          {heading}
        </p>
        {items.map((r) => {
          const idx = results.indexOf(r);
          const Icon = r.icon;
          const active = idx === selected;
          return (
            <button
              key={r.id}
              type="button"
              data-selected={active || undefined}
              onMouseEnter={() => setSelected(idx)}
              onClick={() => go(r)}
              className={cn(
                'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors duration-100',
                active
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                  : 'text-slate-600 dark:text-slate-300',
              )}
            >
              <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
              <span className="min-w-0 flex-1 truncate font-semibold">{r.label}</span>
              {r.sub ? (
                <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">{r.sub}</span>
              ) : null}
              {active ? (
                <CornerDownLeft className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
              ) : null}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onClose} className="max-w-xl overflow-hidden p-0">
      <div className="flex items-center gap-3 border-b border-slate-200 px-4 dark:border-slate-800">
        <Search className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('palette.placeholder')}
          aria-label={t('palette.search')}
          className="h-12 w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
        />
        <kbd className="hidden shrink-0 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400 sm:inline dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500">
          Esc
        </kbd>
      </div>
      <div ref={listRef} className="max-h-80 overflow-y-auto pb-2">
        {results.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('palette.noResults')}
          </p>
        ) : (
          <>
            {renderSection('nav', t('palette.navigation'))}
            {renderSection('order', t('palette.supplyOrders'))}
          </>
        )}
      </div>
      <div className="flex items-center gap-4 border-t border-slate-200 px-4 py-2 text-[11px] text-slate-400 dark:border-slate-800 dark:text-slate-500">
        <span className="flex items-center gap-1">
          <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 font-semibold dark:border-slate-700 dark:bg-slate-800">
            ↑↓
          </kbd>
          {t('palette.hintNavigate')}
        </span>
        <span className="flex items-center gap-1">
          <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 font-semibold dark:border-slate-700 dark:bg-slate-800">
            ↵
          </kbd>
          {t('palette.hintOpen')}
        </span>
        <span className="flex items-center gap-1">
          <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 font-semibold dark:border-slate-700 dark:bg-slate-800">
            Esc
          </kbd>
          {t('palette.hintClose')}
        </span>
      </div>
    </Modal>
  );
}
